// What the screen, agents and the server share about files: the request to
// reserve an upload, the DTOs, and the MIME rules. Pure code (no Node, no
// database), so the screen can import it too.
import { z } from "zod";

/**
 * Types that may be shown inline. Everything else is served as
 * `application/octet-stream` with `attachment`. Videos are listed so they play
 * in `<video>` (Safari will not play octet-stream).
 */
export const INLINE_MIMES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/avif",
  "application/pdf",
  "video/mp4",
  "video/webm",
  "video/quicktime",
] as const;

export const FILE_NAME_MAX = 255;

/** SHA-256 of the content, lowercase hex. */
export const FILE_HASH_RE = /^[0-9a-f]{64}$/;

/**
 * Size at which a client refuses before reading a file to hash it. Not the
 * server's limit (that is the host's `FileLimits`); only a guard against
 * loading something huge. Raise it if a host's limit goes above it.
 */
export const FILE_CLIENT_READ_LIMIT = 256 * 1024 * 1024;

export function isInlineMime(mime: string): boolean {
  return (INLINE_MIMES as readonly string[]).includes(mime);
}

/** Drawn as an image. `image/svg+xml` is not: it is never served inline. */
export function isImageFileMime(mime: string): boolean {
  return mime.startsWith("image/") && isInlineMime(mime);
}

/** Drawn as a video. */
export function isVideoFileMime(mime: string): boolean {
  return mime.startsWith("video/") && isInlineMime(mime);
}

/** `type/subtype` in lowercase without parameters, or null if malformed. */
export function normalizeMime(input: string): string | null {
  const base = input.split(";")[0].trim().toLowerCase();
  return /^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*$/.test(base)
    ? base
    : null;
}

const MIME_BY_EXT: Record<string, string> = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".gif": "image/gif",
  ".webp": "image/webp",
  ".avif": "image/avif",
  ".svg": "image/svg+xml",
  ".pdf": "application/pdf",
  ".mp4": "video/mp4",
  ".webm": "video/webm",
  ".mov": "video/quicktime",
  ".csv": "text/csv",
  ".txt": "text/plain",
  ".md": "text/markdown",
  ".json": "application/json",
  ".zip": "application/zip",
};

/** The type a file name suggests; `application/octet-stream` when unknown. */
export function mimeFromFileName(name: string): string {
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 ? name.slice(dot).toLowerCase() : "";
  return MIME_BY_EXT[ext] ?? "application/octet-stream";
}

/**
 * A Content-Disposition value. `filename*` (UTF-8) is what browsers use; the
 * ASCII `filename` before it is for old ones (a name with other characters
 * becomes `file.<ext>`).
 */
export function contentDispositionValue(
  kind: "inline" | "attachment",
  name: string,
): string {
  const plain = (s: string) => /^[\x20-\x7e]*$/.test(s) && !/["\\]/.test(s);
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
  const fallback = plain(name) ? name : `file${plain(ext) ? ext : ""}`;
  const encoded = encodeURIComponent(name).replace(
    /[!'()*]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  );
  return `${kind}; filename="${fallback}"; filename*=UTF-8''${encoded}`;
}

export const FileName = z
  .string()
  .trim()
  .min(1)
  .max(FILE_NAME_MAX)
  // biome-ignore lint/suspicious/noControlCharactersInRegex: a file name must not contain them
  .refine((v) => !/[/\\\u0000-\u001f\u007f]/.test(v), {
    error: "a file name may not contain slashes or control characters",
  });

/** POST /api/files: reserve an upload. */
export const CreateFileRequest = z
  .object({
    name: FileName,
    mime: z.string().transform((v, ctx) => {
      const mime = normalizeMime(v);
      if (!mime) ctx.addIssue({ code: "custom", message: "not a MIME type" });
      return mime ?? "";
    }),
    size: z.number().int().min(1),
    /** The content's SHA-256: the key for reusing the same content. The upload URL fixes it. */
    hash: z.string().regex(FILE_HASH_RE),
    width: z.number().int().min(1).max(10000).optional(),
    height: z.number().int().min(1).max(10000).optional(),
  })
  .refine((v) => (v.width === undefined) === (v.height === undefined), {
    error: "give both width and height or neither",
  });
export type CreateFileRequestT = z.infer<typeof CreateFileRequest>;

export interface FileDTO {
  id: string;
  name: string;
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
  status: "pending" | "ready";
  uploadedBy: string | null;
  createdAt: string;
}

/** Where to PUT the body. null in a reserve answer means the content was reused. */
export interface FileUploadDTO {
  url: string;
  method: "PUT";
  headers: Record<string, string>;
  expiresAt: string;
}

/** Drawn as an image or a video in a body (`!`, `kind=video`), from the type. */
export function fileEmbedOf(mime: string): { image: boolean; video: boolean } {
  const video = isVideoFileMime(mime);
  return { image: video || isImageFileMime(mime), video };
}

/**
 * A name inside `[...]` of the file syntax. The only escapes are `\\` and `\]`;
 * a line break cannot be escaped, so it becomes a space.
 */
export function escapeFileName(name: string): string {
  return name.replace(/\r\n|[\r\n]/g, " ").replace(/[\\\]]/g, (c) => `\\${c}`);
}

/**
 * The line that puts a file into a body:
 *   image  ![name](trameria://file/<id>)
 *   video  ![name](trameria://file/<id>?kind=video)
 *   other  [name](trameria://file/<id>)
 * The Document part reads and writes the full syntax (widths too) and builds on
 * this; nothing else should spell the line out.
 */
export function fileMarkdownLine(file: {
  id: string;
  name: string;
  mime: string;
}): string {
  const { image, video } = fileEmbedOf(file.mime);
  return `${image ? "!" : ""}[${escapeFileName(file.name)}](trameria://file/${file.id.toLowerCase()}${video ? "?kind=video" : ""})`;
}

const FILE_URL_RE =
  /trameria:\/\/file\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi;

/** The file ids a text names with `trameria://file/<id>` (lower case, once each, in order). */
export function fileIdsIn(text: string | null | undefined): string[] {
  if (!text) return [];
  return [
    ...new Set([...text.matchAll(FILE_URL_RE)].map((m) => m[1].toLowerCase())),
  ];
}
